import { FroalaOptions } from 'froala-editor';

import { ReferenceWithNumber } from '@treetop/shared/model';

export function cleanClipboardContent(clipboard_html: string, options: Partial<FroalaOptions>): string {
  const doc = getDoc(clipboard_html);

  if (!options.isPlainCopyEnabled) {
    const nodesToModify: HTMLElement[] = getPTagsFromTdElements(doc);
    adaptNodesForPInTd(nodesToModify);
  } else {
    removeAllPTagsAndInsertBr(doc);
  }

  return doc.body.innerHTML;
}

export function handleKeyDown(e: KeyboardEvent, options: Partial<FroalaOptions>) {
  options.isPlainCopyEnabled = (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'v';
}

export function disablePlainCopyOnKeyup(e: KeyboardEvent, options: Partial<FroalaOptions>) {
  options.isPlainCopyEnabled = false;
}

function getDoc(clipboard_html: string): Document {
  const parser = new DOMParser();

  return parser.parseFromString(clipboard_html, 'text/html');
}

function getTreeWalker(doc: Document): TreeWalker {
  const htmlElement = doc.body.firstChild;

  return document.createTreeWalker(htmlElement);
}

function getPTagsFromTdElements(doc: Document): HTMLElement[] {
  const walker = getTreeWalker(doc);
  let currentNode: Node | null = walker.nextNode();
  const nodesToModify: HTMLElement[] = [];

  while (currentNode) {
    const currentElement = currentNode as HTMLElement;

    if (currentNode.nodeName === 'P' && currentElement.parentElement?.nodeName === 'TD' && currentElement.attributes.length === 0) {
      nodesToModify.push(currentElement);
    }
    currentNode = walker.nextNode();
  }

  return nodesToModify;
}

function adaptNodesForPInTd(nodesToModify: HTMLElement[]): void {
  nodesToModify.forEach((pElement) => {
    const parentTd = pElement.parentElement;

    if (parentTd && parentTd.nodeName === 'TD') {
      while (pElement.firstChild) {
        parentTd.insertBefore(pElement.firstChild, pElement);
      }
      parentTd.removeChild(pElement);
    }
  });
}

function removeAllPTagsAndInsertBr(doc: Document): void {
  const pElements = doc.querySelectorAll('p');
  pElements.forEach((p, index) => {
    const parent = p.parentElement;

    if (parent) {
      while (p.firstChild) {
        const child = p.firstChild;
        parent.insertBefore(child, p);

        if (child.nodeType === Node.TEXT_NODE && child.nextSibling) {
          parent.insertBefore(document.createElement('br'), p);
        }
      }

      parent.removeChild(p);
    }
  });

  const brElementsForInSeries = doc.querySelectorAll('br');
  brElementsForInSeries.forEach((br, index) => {
    if (br.nextSibling && br.nextSibling.nodeName === 'BR') {
      br.remove();
    }
  });

  const allbrElements = doc.querySelectorAll('br');
  allbrElements.forEach((br, index) => {
    if (index === allbrElements.length - 1 && !br.nextSibling) {
      br.remove();
    }
  });
}

export const getAllReferenceIdsFromText = (text): string[] => {
  const regex = /data-reference-id="([^"]+)"/g;
  return Array.from(text?.matchAll(regex) ?? [], (match) => match[1]);
};

export const updateReferenceNumbersInText = (text: string, referencesWithNumber: ReferenceWithNumber[]): string => {
  if (referencesWithNumber.length === 0) {
    return text;
  }

  let updatedContent = text;

  for (const { id, referenceNumber } of referencesWithNumber) {
    const regex = new RegExp(`(<span[^>]*data-reference-id=["']${id}["'][^>]*>\\s*)\\[[^]]*\\](\\s*<\\/span>)`, 'g');
    updatedContent = updatedContent.replace(regex, `$1[${referenceNumber}]$2`);
  }

  return updatedContent;
};

export function placePopupBottomLeftOfEditor(editor: any, popupId: string, buttonCommand: string): void {
  const popupElement = getElement(editor.popups.get(popupId));
  const toolbarElement = getElement(editor.$tb);

  if (!popupElement || !toolbarElement) return;

  const editorBoxElement = getElement(editor.$box) || toolbarElement.closest<HTMLElement>('.fr-box') || getElement(editor.el);

  if (!editorBoxElement) return;

  if (popupElement.parentElement !== document.body) {
    document.body.appendChild(popupElement);
  }

  const margin = 8;
  const buttonElement = toolbarElement.querySelector<HTMLElement>(`[data-cmd="${buttonCommand}"]`);
  const anchorRect = (buttonElement ?? toolbarElement).getBoundingClientRect();
  const popupRect = popupElement.getBoundingClientRect();
  let top = anchorRect.bottom;
  let cssRight = window.innerWidth - anchorRect.right;
  cssRight = Math.max(margin, Math.min(cssRight, window.innerWidth - popupRect.width - margin));
  top = Math.max(margin, Math.min(top, window.innerHeight - popupRect.height - margin));

  Object.assign(popupElement.style, {
    position: 'fixed',
    left: 'auto',
    right: `${cssRight}px`,
    top: `${top}px`,
    bottom: 'auto',
  });
}

function getElement(value: any): HTMLElement | null {
  if (!value) return null;

  if (value instanceof HTMLElement) {
    return value;
  }

  if (value[0] instanceof HTMLElement) {
    return value[0];
  }

  if (typeof value.get === 'function') {
    const el = value.get(0);
    if (el instanceof HTMLElement) {
      return el;
    }
  }

  return null;
}
